<script lang="ts">
  let { data } = $props()

  let posts = $derived(data.posts)
  const canonicalUrl = "https://www.promoamp.com/blog"
  const pageTitle = "Giveaway Marketing Guides & Growth Strategies | Promo Amp"
  const pageDescription =
    "Practical giveaway marketing guides, contest strategies, prize ideas, platform comparisons, and growth advice from the Promo Amp team."
  const socialImage = "https://img.promoamp.com/og_image.png"
  const structuredData = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        "@id": `${canonicalUrl}#blog`,
        url: canonicalUrl,
        name: "The Promo Amp Blog",
        description: pageDescription,
        publisher: {
          "@type": "Organization",
          "@id": "https://www.promoamp.com/#organization",
          name: "Promo Amp",
          url: "https://www.promoamp.com/",
          logo: {
            "@type": "ImageObject",
            url: "https://www.promoamp.com/logo.png",
          },
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://www.promoamp.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Blog",
            item: canonicalUrl,
          },
        ],
      },
    ],
  }).replace(/</g, "\\u003c")
  const structuredDataMarkup =
    `<script type="application/ld+json">${structuredData}<` + "/script>"

  // Function to handle image loading errors
  function handleImageError(event: Event) {
    const img = event.target as HTMLImageElement
    img.style.display = "none"
    const fallback = img.nextElementSibling as HTMLElement
    if (fallback) {
      fallback.style.display = "flex"
    }
  }
</script>

<svelte:head>
  <title>{pageTitle}</title>
  <meta name="description" content={pageDescription} />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href={canonicalUrl} />

  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Promo Amp" />
  <meta property="og:title" content={pageTitle} />
  <meta property="og:description" content={pageDescription} />
  <meta property="og:url" content={canonicalUrl} />
  <meta property="og:image" content={socialImage} />
  <meta property="og:image:alt" content="Promo Amp giveaway marketing blog" />

  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={pageTitle} />
  <meta name="twitter:description" content={pageDescription} />
  <meta name="twitter:image" content={socialImage} />
  <meta name="twitter:image:alt" content="Promo Amp giveaway marketing blog" />

  <!-- eslint-disable-next-line svelte/no-at-html-tags -->
  {@html structuredDataMarkup}
</svelte:head>

<div
  class="max-w-6xl mx-auto pt-28 pb-12 md:pt-32 md:pb-20 px-3 sm:px-4 lg:px-6"
>
  <!-- Section header -->
  <div class="text-center pb-12 md:pb-16">
    <h1
      class="text-2xl sm:text-3xl md:text-5xl pb-4 font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent px-2"
    >
      The Promo Amp Blog
    </h1>
    <p
      class="text-sm sm:text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-2"
    >
      Explore practical campaign planning, prize, promotion and winner-selection
      guides from the Promo Amp team. Outcomes depend on your offer, audience
      and execution.
    </p>
  </div>

  <!-- Blog posts grid -->
  <div class="grid gap-4 sm:gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
    {#each posts as post}
      <article
        class="cursor-pointer group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200 overflow-hidden border border-gray-100 w-full"
      >
        <!-- Blog image -->
        <div class="aspect-video overflow-hidden bg-gray-100 relative">
          {#if post.image}
            <img
              class="w-full h-full object-cover"
              src={post.image}
              alt={post.title}
              loading="lazy"
              onerror={handleImageError}
            />
            <!-- Fallback for failed images -->
            <div
              class="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 items-center justify-center hidden"
            >
              <div class="text-4xl opacity-40">📝</div>
            </div>
          {:else}
            <div
              class="w-full h-full bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center"
            >
              <div class="text-4xl opacity-40">📝</div>
            </div>
          {/if}
        </div>

        <div class="p-4 sm:p-6">
          <!-- Author info -->
          <div class="flex items-center gap-3 mb-4">
            {#if post.author.avatar_url}
              <img
                class="rounded-full w-8 h-8 object-cover flex-shrink-0"
                src={post.author.avatar_url}
                alt={post.author.name}
                loading="lazy"
              />
            {:else}
              <div
                class="rounded-full w-8 h-8 bg-gray-300 flex items-center justify-center flex-shrink-0"
              >
                <span class="text-xs font-medium text-gray-600">
                  {post.author.name?.charAt(0) || "?"}
                </span>
              </div>
            {/if}
            <div class="text-sm text-gray-600 truncate">{post.author.name}</div>
            {#if post.date}
              <div class="text-sm text-gray-400 hidden sm:block">•</div>
              <div class="text-sm text-gray-400 hidden sm:block">
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </div>
            {/if}
          </div>

          <!-- Date on mobile (separate line) -->
          {#if post.date}
            <div class="text-xs text-gray-400 mb-3 sm:hidden">
              {new Date(post.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
          {/if}

          <!-- Title -->
          <h2
            class="text-lg sm:text-xl font-bold mb-3 leading-tight hover:text-blue-600 transition-colors duration-200"
          >
            <a class="hover:underline" href={`/blog/${post.slug}`}>
              {post.title}
            </a>
          </h2>

          <!-- Description -->
          <p
            class="text-sm sm:text-base text-gray-600 mb-4 line-clamp-3 leading-relaxed"
          >
            {post.description}
          </p>

          <!-- Read more link -->
          <a
            class="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors duration-200"
            href={`/blog/${post.slug}`}
          >
            Read more
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              ></path>
            </svg>
          </a>
        </div>
      </article>
    {/each}
  </div>

  <!-- Empty state if no posts -->
  {#if posts.length === 0}
    <div class="text-center py-16 px-4">
      <div class="text-6xl mb-4">📝</div>
      <h3 class="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
        No blog posts yet
      </h3>
      <p class="text-gray-600">
        Check back soon for our latest insights and updates!
      </p>
    </div>
  {/if}
</div>

<style>
  .line-clamp-3 {
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
